// siteAlbedo.ts - a region orthoimage as albedo for the pyramid leaves under it.
//
// A site bake's leaves are height only (docs/ATLAS-TERRAIN.md, "Tile tree
// contract"), so a leaf inherits imagery from the L6 albedo floor: 163 m per
// texel over ground sampled at 10 m. The region's own orthoimage is 6 m per
// texel over the same ground and is already in the tree as one whole-file PNG,
// so the leaves inside the footprint draw out of it and the ones outside keep
// what they inherit.
//
// One image, many tiles: the part of it a tile covers is an affine on the uv
// attribute (`SiteAlbedoRef`), not a texture per tile. The affine is exact to
// within the region frame's own curvature - the frame is planar in metres about
// the site centre and the only nonlinearity across a tile is the cubesphere's
// own, which `worstResidualTexels` reports rather than assumes. That number is
// a bound on the AFFINE and not on registration; the frame's agreement with the
// bake's projection is `tools/dtm/region-tiles-check.py`'s frame reading.

import * as THREE from 'three';
import { loadFlippedBitmap } from './imageBitmap.ts';
import { directionForFaceUv, latLonForDirection } from './tileTree.ts';
import type { TileBounds } from './tileTree';
import type { SiteAlbedoRef } from './tileMesh';
import { wrap180HalfOpen } from '../math/angles.ts';
import { readableCanvas2d } from './canvas2d.ts';
import { polarFrameOf, polarRegionXZ, type PolarFrame, type RegionProjection } from './regionFrame.ts';

const DEG = 180 / Math.PI;

/** The region frame a `meta.json` describes: planar metres about the site centre. */
interface SiteFrame {
  readonly centerLatDeg: number;
  readonly centerLonDeg: number;
  readonly widthM: number;
  readonly heightM: number;
  /**
   * Metres per degree on the bake's own grid, which is the product's
   * equirectangular projection: constant in both axes, the longitude one
   * carrying a cos(the PRODUCT's centre latitude) that a sphere reads 1.8%
   * differently at Jezero.
   */
  readonly mPerDegLat: number;
  readonly mPerDegLon: number;
  /** A polar stereographic bake's grid, which no metres-per-degree pair describes. */
  readonly polar: PolarFrame | null;
  /** Metres per texel, per axis: a region bake need not be square in either. */
  readonly texelUM: number;
  readonly texelVM: number;
}

interface SiteEntry {
  frame: SiteFrame | null;
  texture: THREE.Texture | null;
  /** A low-pass of the same image, at the inherited colour product's own scale. */
  lowPass: THREE.Texture | null;
  /** The product's own mean, raw, for whoever shades by a ratio against it. */
  meanRaw: number;
  bitmap: ImageBitmap | null;
  /** Requested, so a miss is not retried every frame. */
  asked: boolean;
}

/**
 * Where the orthoimage's job starts: one Viking L6 albedo texel, 163 m.
 *
 * A leaf shades the colour it inherits by `ortho / lowPass(ortho)`, so the cut
 * is what decides which of the two products supplies a given spatial frequency.
 * Cutting at the colour product's own texel means the ortho supplies everything
 * finer than the colour and nothing coarser: divide by a GLOBAL mean instead and
 * a footprint over dark ground reads 5.3 grey levels below the colour around it,
 * which is the footprint edge as a step all over again.
 *
 * Exported because the walkaround's own mesh draws the same ground out of the
 * same file and has to cut it at the same place (`orthoLowPassOf` in
 * src/atlas.ts); two cuts would be two products again.
 */
export const LOWPASS_M = 160;

/**
 * The same image, boxed down to one sample per `LOWPASS_M` of ground.
 *
 * Tagged sRGB like the image it came from, so the sampler decodes both the same
 * way and the ratio in the shader is a ratio of reflectances. Bilinear over a
 * smooth field rather than a constant per tile: a constant per tile would
 * normalise each leaf to its own mean and put a step of its own at every leaf
 * boundary, which is the fault this is here to remove.
 */
function lowPassOf(bitmap: ImageBitmap, widthM: number, heightM: number):
{ texture: THREE.Texture; meanRaw: number } | null {
  const w = Math.max(2, Math.round(widthM / LOWPASS_M));
  const h = Math.max(2, Math.round(heightM / LOWPASS_M));
  const made = readableCanvas2d(w, h);
  if (!made) return null;
  made.ctx.drawImage(bitmap, 0, 0, w, h);
  const data = made.ctx.getImageData(0, 0, w, h).data;
  // The same bytes, averaged: what the walkaround's own shader needs to shade
  // the SAME ground by the same ratio, and it samples the orthoimage with no
  // transfer on it, so the mean it wants is the raw one.
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) sum += data[i]! / 255;
  const meanRaw = Math.max(1e-3, sum / (data.length / 4));
  const texture = new THREE.DataTexture(
    new Uint8Array(data.buffer.slice(0)), w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.name = 'atlas-site-ortho-lowpass';
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  // The rows are the bitmap's own, which `loadFlippedBitmap` already turned the
  // way `texUv` reads them, so neither texture applies the unpack flip.
  texture.flipY = false;
  texture.needsUpdate = true;
  return { texture, meanRaw };
}

export interface SiteAlbedoConfig {
  readonly radiusM: number;
  /** The region directory for a site id, as `dtmUrl` builds it. */
  urlFor(siteId: string): string;
  readonly anisotropy?: number;
  /**
   * Upload the image now rather than on the frame that first draws it.
   *
   * Jezero's orthoimage is 1906x3102, and even decoded off the main thread its
   * blit and mip chain do not fit in a frame alongside a draw. This lands it on
   * the frame it arrives, which the governor already excludes.
   */
  uploadTexture?: (texture: THREE.Texture) => void;
  /** Called when a site's image lands, so the meshes waiting on it can rebuild. */
  onReady?: (siteId: string) => void;
  onError?: (siteId: string, err: unknown) => void;
}

export interface SiteAlbedoHandle {
  /**
   * The region image over this tile, or null.
   *
   * Null when the site's image has not landed yet, when the tile is not wholly
   * inside the footprint, or when there is no such site - all three mean the
   * tile draws with what it inherited. The first call for a site starts the
   * fetch and returns null; `onReady` is what says to ask again.
   */
  refFor(siteId: string, bounds: TileBounds): SiteAlbedoRef | null;
  /**
   * The site orthoimage's own mean, raw, or null before it has landed.
   *
   * The walkaround's mesh shades the same ground by a ratio against this, so it
   * reads the number here rather than reducing a 4,096 px texture of its own on
   * the frame that builds the mesh.
   */
  meanRawFor(siteId: string): number | null;
  /** How far the affine is from the true mapping, in texels, worst tile so far. */
  readonly worstResidualTexels: number;
  readonly loaded: number;
  dispose(): void;
}

/**
 * Region-frame texture coordinates for a body-fixed direction.
 *
 * The engine's region frame, the same one `latLonAt` in `src/atlas/plotGeometry.ts` inverts:
 * +x east, +z south, the image centred on the site with its first row north,
 * which the decode's own flip puts at v = 1. The two metres-per-degree are the
 * BAKE's, off `meta.frame` - a sphere's own scale misses the product's
 * projection by 96 m in x at Jezero.
 */
function texUv(f: SiteFrame, dir: readonly [number, number, number],
): [number, number] {
  const ll = latLonForDirection(dir);
  if (f.polar) {
    const p = polarRegionXZ(f.polar, f.centerLatDeg, f.centerLonDeg, ll.latRad * DEG, ll.lonRad * DEG);
    return [0.5 + p.x / f.widthM, 0.5 - p.z / f.heightM];
  }
  const zM = (f.centerLatDeg - ll.latRad * DEG) * f.mPerDegLat;
  const xM = wrap180HalfOpen(ll.lonRad * DEG - f.centerLonDeg) * f.mPerDegLon;
  return [0.5 + xM / f.widthM, 0.5 - zM / f.heightM];
}

export function createSiteAlbedo(config: SiteAlbedoConfig): SiteAlbedoHandle {
  const sites = new Map<string, SiteEntry>();
  let worst = 0;
  let live = true;
  // The meta and the image are two fetches over a 1906x3102 PNG, so closing
  // the Atlas mid-descent would otherwise leave both running and decode into a
  // texture nothing can dispose.
  const lifetime = new AbortController();

  async function load(siteId: string, entry: SiteEntry): Promise<void> {
    const base = config.urlFor(siteId);
    const res = await fetch(`${base}/meta.json`, { signal: lifetime.signal });
    if (!res.ok) throw new Error(`${base}/meta.json: HTTP ${res.status}`);
    const meta = await res.json() as {
      center_lat_deg?: number; center_lon_deg?: number;
      bbox_m?: { width?: number; height?: number };
      albedo_texture?: { file?: string; width?: number; height?: number };
      frame?: Partial<RegionProjection>;
    };
    const file = meta.albedo_texture?.file;
    const width = meta.bbox_m?.width;
    const height = meta.bbox_m?.height;
    const lat = meta.center_lat_deg;
    const lon = meta.center_lon_deg;
    if (!file || !width || !height || lat === undefined || lon === undefined) {
      throw new Error(`${base}/meta.json has no albedo frame`);
    }
    // Decoded off the main thread: through `TextureLoader` the PNG's decode
    // landed inside the upload's own `texImage2D` and read 50 ms on the frame
    // the first site leaf drew (`docs/roadmap/07-performance-and-instruments.md`).
    const bitmap = await loadFlippedBitmap(`${base}/${file}`, lifetime.signal);
    if (!live) {
      bitmap.close();
      return;
    }
    const texture = new THREE.Texture(bitmap);
    // sRGB, because it is a picture of the ground and the sampler's decode is
    // what makes `ortho / mean` a ratio of reflectances rather than of bytes.
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.name = 'atlas-site-ortho';
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.anisotropy = config.anisotropy ?? 1;
    // The decode flipped the rows, so the unpack flip must not undo it; either
    // way the source's first row samples at v = 1, which is what `texUv` says.
    texture.flipY = false;
    texture.needsUpdate = true;
    entry.bitmap = bitmap;
    const low = lowPassOf(bitmap, width, height);
    entry.lowPass = low?.texture ?? null;
    entry.meanRaw = low?.meanRaw ?? 0;
    config.uploadTexture?.(texture);
    if (entry.lowPass) config.uploadTexture?.(entry.lowPass);
    // The spherical pair is the fallback for a region baked before the bake
    // wrote its own; it is the frame this module used to assume everywhere.
    const perDeg = (config.radiusM * Math.PI) / 180;
    entry.frame = {
      centerLatDeg: lat,
      centerLonDeg: lon,
      widthM: width,
      heightM: height,
      mPerDegLat: meta.frame?.m_per_deg_lat ?? perDeg,
      mPerDegLon: meta.frame?.m_per_deg_lon
        ?? perDeg * Math.cos((lat * Math.PI) / 180),
      polar: polarFrameOf(meta.frame),
      texelUM: width / Math.max(1, meta.albedo_texture?.width ?? bitmap.width),
      texelVM: height / Math.max(1, meta.albedo_texture?.height ?? bitmap.height),
    };
    entry.texture = texture;
  }

  return {
    refFor(siteId: string, bounds: TileBounds): SiteAlbedoRef | null {
      let entry = sites.get(siteId);
      if (!entry) {
        entry = { frame: null, texture: null, lowPass: null, meanRaw: 0, bitmap: null, asked: false };
        sites.set(siteId, entry);
      }
      if (!entry.asked) {
        entry.asked = true;
        void load(siteId, entry).then(
          () => { if (live) config.onReady?.(siteId); },
          (err: unknown) => { if (live) config.onError?.(siteId, err); },
        );
      }
      const { frame, texture, lowPass } = entry;
      if (!frame || !texture || !lowPass) return null;
      const at = (a: number, b: number): [number, number] => texUv(frame,
        directionForFaceUv(bounds.face,
          bounds.u0 + a * (bounds.u1 - bounds.u0),
          bounds.v0 + b * (bounds.v1 - bounds.v0)));
      const c00 = at(0, 0);
      const c10 = at(1, 0);
      const c01 = at(0, 1);
      const c11 = at(1, 1);
      // Wholly inside the image, so no tile draws the edge texel stretched
      // across ground the orthoimage never covered.
      for (const c of [c00, c10, c01, c11]) {
        if (c[0] < 0 || c[0] > 1 || c[1] < 0 || c[1] > 1) return null;
      }
      const du: [number, number] = [c10[0] - c00[0], c10[1] - c00[1]];
      const dv: [number, number] = [c01[0] - c00[0], c01[1] - c00[1]];
      // The affine is fixed by three corners; the fourth is where its error is
      // largest, which is the whole of what the planar frame costs.
      const texels = Math.hypot(
        ((c00[0] + du[0] + dv[0] - c11[0]) * frame.widthM) / frame.texelUM,
        ((c00[1] + du[1] + dv[1] - c11[1]) * frame.heightM) / frame.texelVM,
      );
      if (texels > worst) worst = texels;
      return {
        id: `site:${siteId}`,
        map: texture,
        lowPass,
        uv: [c00[0], du[0], dv[0], c00[1], du[1], dv[1]],
      };
    },
    meanRawFor(siteId: string): number | null {
      const entry = sites.get(siteId);
      return entry && entry.meanRaw > 0 ? entry.meanRaw : null;
    },
    get worstResidualTexels(): number {
      return worst;
    },
    get loaded(): number {
      let n = 0;
      for (const e of sites.values()) if (e.texture) n++;
      return n;
    },
    dispose(): void {
      live = false;
      lifetime.abort();
      for (const e of sites.values()) {
        e.texture?.dispose();
        e.lowPass?.dispose();
        e.bitmap?.close();
        e.texture = null;
        e.lowPass = null;
        e.meanRaw = 0;
        e.bitmap = null;
        e.frame = null;
      }
      sites.clear();
      worst = 0;
    },
  };
}
